import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Phone, User, Truck, Wallet, Receipt, IndianRupee, Loader2, Navigation, PackageCheck, RefreshCw, Undo2 } from "lucide-react";
import { api, inr } from "../../lib/api";
import { openMap } from "../../lib/openMap";
import { toast } from "sonner";
import CallButton from "../../components/CallButton";
import { useTenant } from "../../contexts/TenantContext";
import { useSubmitGuard } from "../../lib/useSubmitGuard";
import LseAdminOrderPanel from "../../components/lse/LseAdminOrderPanel";

const STATUS_SEQ = ["PLACED", "CONFIRMED", "PREPARING", "READY", "OUT_FOR_DELIVERY", "DELIVERED"];
const TERMINAL = new Set(["DELIVERED", "CANCELLED", "REJECTED"]);

export default function AdminOrderDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { tenant } = useTenant();
    const [o, setO] = useState(null);

    const load = async () => {
        try { const { data } = await api.get(`/admin/orders/${id}`); setO(data); }
        catch (e) { toast.error(e?.response?.data?.detail || "Could not load order"); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    // Bug found in production: `disabled={busy}` alone still let a fast
    // double-click fire two PATCH requests (each triggering its own
    // order-status-change push) before React's re-render actually
    // applied the disabled attribute. useSubmitGuard adds a synchronous
    // ref check ahead of that race — see its docstring.
    const [busy, run] = useSubmitGuard();
    const advance = (next) => run(async () => {
        try {
            await api.patch(`/admin/orders/${id}/status?status=${next}`);
            toast.success(`→ ${next.replace("_", " ")}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    });

    const cancel = async () => {
        if (!confirm("Cancel this order?")) return;
        await advance("CANCELLED");
    };

    if (!o) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" /></div>;

    const t = o.totals || {};
    const adr = o.delivery_address || {};
    const next = STATUS_SEQ[STATUS_SEQ.indexOf(o.status) + 1];
    const isTerminal = TERMINAL.has(o.status);
    const cdc = !!o.cdc_enabled;

    return (
        <div>
            <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground mb-3"><ArrowLeft size={14} /> Back to orders</button>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">{o.order_unique_id}</h1>
                    <div className="text-sm text-muted-foreground mt-1 inline-flex items-center gap-2 flex-wrap">
                        <Badge>{o.status}</Badge>
                        {o.zone_name && <Badge tone="sky"><MapPin size={10} className="inline mr-1" />{o.zone_name}</Badge>}
                        {cdc && <Badge tone="violet">CDC</Badge>}
                        {o.return_status && <Badge tone={RETURN_TONE[o.return_status]}>Return {o.return_status}</Badge>}
                        <span>{o.payment_method} · {o.payment_status}</span>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {!isTerminal && next && (
                        <button data-testid="advance-btn" disabled={busy} onClick={() => advance(next)}
                            className="bg-primary text-primary-foreground rounded-xl px-4 py-2 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50">
                            {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                            Mark {next.replace("_", " ")}
                        </button>
                    )}
                    {!isTerminal && (
                        <button data-testid="cancel-btn" onClick={cancel} className="border border-destructive text-destructive rounded-xl px-3 py-2 text-sm font-semibold">Cancel</button>
                    )}
                </div>
            </div>

            {/* Location Service Engine tenants only (renders nothing otherwise). */}
            <LseAdminOrderPanel orderId={o.id} onChanged={load} />

            <div className="grid lg:grid-cols-[1fr_360px] gap-4 mt-6">
                {/* LEFT: items + pricing */}
                <div className="space-y-4">
                    <Section title="Items" testid="order-items">
                        <ul className="divide-y divide-border">
                            {(o.items || []).map((it) => (
                                <li key={it.product_id + (it.variant_id || "")} className="flex items-center gap-3 py-3">
                                    <span className={`food-mark ${it.food_type === "NON_VEG" ? "non-veg" : "veg"}`} />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-sm">{it.name}</p>
                                        <p className="text-xs text-muted-foreground">{inr(it.selling_price)} × {it.quantity}</p>
                                    </div>
                                    <div className="text-sm font-bold">{inr((it.selling_price || 0) * (it.quantity || 1))}</div>
                                </li>
                            ))}
                        </ul>
                    </Section>

                    <Section title="Billing breakdown" testid="billing-breakdown" icon={<Receipt size={14} />}>
                        <Row k="Subtotal (items)" v={inr(t.subtotal)} />
                        {t.packaging_fee > 0 && <Row k="Packaging" v={inr(t.packaging_fee)} />}
                        <Row k="Delivery fee" v={t.delivery_fee === 0 ? <em className="not-italic text-emerald-700 font-semibold">FREE</em> : inr(t.delivery_fee)} />
                        {(t.delivery_gst || 0) > 0 && <Row k="Delivery GST" v={inr(t.delivery_gst)} />}
                        {(t.tax || 0) > 0 && <Row k="Item GST" v={inr(t.tax)} />}
                        {(t.additional_charges || []).map((c) => (
                            <Row key={c.id} k={`${c.name} ${c.goes_to === "RESTAURANT" ? "(merchant)" : "(admin)"}`} v={inr(c.amount)} />
                        ))}
                        {(t.discount || 0) > 0 && <Row k="Discount" v={<span className="text-emerald-700">- {inr(t.discount)}</span>} />}
                        {(t.wallet_used || 0) > 0 && <Row k="Wallet used" v={<span className="text-emerald-700">- {inr(t.wallet_used)}</span>} />}
                        <div className="border-t border-border my-2" />
                        <Row k={<strong>Grand total</strong>} v={<strong data-testid="grand-total">{inr(t.grand_total)}</strong>} />
                    </Section>

                    <Section title="Payout split" testid="payout-split" icon={<IndianRupee size={14} />}>
                        {(() => {
                            // iter134e8 → iter134e10 — Break the admin
                            // cut into its constituent parts AND surface
                            // the delivery-fee destination + commission
                            // % so operators can reconcile every rupee
                            // without mental math (user-reported Feb
                            // 2026).
                            //
                            // Order rupee-flow model:
                            //   customer pays  =  merchant_payout          (→ shop)
                            //                  +  admin_earn (commission + admin-side additional_charges)  (→ platform)
                            //                  +  delivery_fee              (→ rider, when borne_by=ADMIN)
                            //                                              (deducted from shop when borne_by=MERCHANT)
                            //                  +  taxes / discounts / wallet
                            const adminCharges = (t.additional_charges || []).filter(
                                (c) => c.goes_to !== "RESTAURANT" && (c.amount || 0) > 0
                            );
                            const adminChargesTotal = adminCharges.reduce(
                                (s, c) => s + (Number(c.amount) || 0), 0,
                            );
                            // ``admin_earn`` is the WHOLE platform wedge —
                            // true commission PLUS any Selling-Price-
                            // Increment markup (a separate pricing lever,
                            // see pricing.py) PLUS admin-side additional
                            // charges. Deriving "commission" as
                            // admin_earn − adminCharges silently folded the
                            // SP markup into the commission figure too,
                            // inflating the displayed % above the actual
                            // configured rate (user-reported: "commission
                            // is 18% but orders show 18.09%" — the 0.09%
                            // here came from an additional rounding step:
                            // admin_earn is snapshotted as a whole-rupee
                            // integer, so back-deriving from it doesn't
                            // even reproduce the exact original percentage).
                            // ``t.commission_amount`` (checkout.py) is the
                            // real, unrounded per-order commission — prefer
                            // it; fall back to the old blended derivation
                            // only for orders placed before that field
                            // existed.
                            const trueCommission = t.commission_amount;
                            const adminWedge = Math.max(0, (Number(t.admin_earn) || 0) - adminChargesTotal);
                            const commission = trueCommission != null
                                ? Math.max(0, Number(trueCommission))
                                : adminWedge;
                            // Remainder of the admin wedge that ISN'T
                            // commission — the SP markup. Zero for orders
                            // where the shop has no Selling-Price-Increment
                            // configured (the common case, and always zero
                            // for legacy orders lacking commission_amount).
                            const spMarkup = trueCommission != null
                                ? Math.max(0, adminWedge - commission) : 0;
                            const subtotal = Number(t.subtotal) || 0;
                            const commissionPct = subtotal > 0
                                ? (commission / subtotal) * 100 : 0;
                            const deliveryFee = Number(t.delivery_fee) || 0;
                            const deliveryToRider = Number(t.delivery_fee_rider) || 0;
                            const borneBy = t.delivery_fee_borne_by;
                            // Human-readable destination for the
                            // delivery fee. Prefer the actual rider
                            // amount when we have it; fall back to the
                            // borne_by hint otherwise.
                            let deliveryDest = null;
                            if (deliveryFee > 0) {
                                if (deliveryToRider > 0) {
                                    deliveryDest = "→ Rider";
                                } else if (borneBy === "MERCHANT") {
                                    deliveryDest = "→ Deducted from shop payout";
                                } else if (borneBy === "ADMIN") {
                                    deliveryDest = "→ Absorbed by admin";
                                }
                            }
                            return (
                                <>
                                    <Row k="Merchant payout" v={<strong data-testid="merchant-payout" className="text-emerald-700">{inr(t.merchant_payout)}</strong>} />
                                    {spMarkup > 0.5 && (
                                        <Row
                                            k={<>Selling price markup <span className="text-[11px] text-muted-foreground ml-1">(platform-retained)</span></>}
                                            v={<span data-testid="admin-sp-markup" className="text-violet-700">{inr(spMarkup)}</span>}
                                        />
                                    )}
                                    <Row
                                        k={<>Business commission {commissionPct > 0 && <span className="text-[11px] text-muted-foreground ml-1">({commissionPct.toFixed(2).replace(/\.?0+$/, "")}%)</span>}</>}
                                        v={<strong data-testid="admin-commission" className="text-violet-700">{inr(commission)}</strong>}
                                    />
                                    {adminCharges.map((c) => (
                                        <Row key={`aec-${c.id || c.name}`}
                                            k={`${c.name} (admin)`}
                                            v={<span data-testid={`admin-charge-${c.id || c.name}`} className="text-violet-700 font-semibold">{inr(c.amount)}</span>} />
                                    ))}
                                    <Row k={<strong>Bizil (admin) total</strong>} v={<strong data-testid="admin-earn" className="text-violet-700">{inr(t.admin_earn)}</strong>} />
                                    {deliveryFee > 0 && (
                                        <Row
                                            k={<>Delivery fee {deliveryDest && <span className="text-[11px] text-muted-foreground ml-1">{deliveryDest}</span>}</>}
                                            v={<span data-testid="delivery-fee-destination" className="text-sky-700 font-semibold">{inr(deliveryFee)}</span>}
                                        />
                                    )}
                                </>
                            );
                        })()}
                        <p className="text-xs text-muted-foreground mt-2">
                            Snapshot taken at checkout using the merchant&apos;s active commission and tier-overrides at that moment.
                        </p>
                    </Section>

                    {cdc ? (
                        <Section title="Driver settlement (CDC)" tone="violet" testid="cdc-section" icon={<Truck size={14} />}>
                            <p className="text-sm">
                                The rider must <strong>pay the restaurant {inr(t.merchant_payout)}</strong> on pickup, and collect{" "}
                                <strong>{inr(t.grand_total)}</strong> from the customer{o.payment_method !== "COD" ? " (already prepaid online)" : ""}.
                            </p>
                        </Section>
                    ) : o.payment_method === "COD" ? (
                        <Section title="Driver settlement (COD)" tone="amber" testid="cod-section" icon={<Wallet size={14} />}>
                            <p className="text-sm">
                                The rider will <strong>collect {inr(t.grand_total)}</strong> from the customer at delivery.
                            </p>
                        </Section>
                    ) : (
                        <Section title="Driver settlement" tone="emerald" testid="prepaid-section" icon={<Wallet size={14} />}>
                            <p className="text-sm">Payment was captured online — no cash handling at delivery.</p>
                        </Section>
                    )}
                </div>

                {/* RIGHT: customer + rider + timeline */}
                <div className="space-y-4">
                    <ReturnCard ret={o.return_request} />

                    <Section title="Customer" testid="customer-card">
                        <p className="font-semibold text-sm inline-flex items-center gap-2"><User size={14} /> {o.customer_name || "—"}</p>
                        <p className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-2"><Phone size={12} /> {o.customer_mobile || "—"}</p>
                        <div className="mt-3 text-xs text-muted-foreground">
                            <p className="font-semibold uppercase tracking-wider text-[10px] mb-1">Deliver to</p>
                            <p className="line-clamp-3">
                                {[adr.line1, adr.line2, adr.city, adr.pincode].filter(Boolean).join(", ")}
                            </p>
                        </div>
                        <ContactRow phone={o.customer_mobile} mapTo={{ lat: adr.lat, lng: adr.lng, label: [adr.line1, adr.city, adr.pincode].filter(Boolean).join(", ") }} testidPrefix="cust" />
                    </Section>

                    <Section title="Shop" testid="restaurant-card">
                        <p className="font-semibold text-sm">{o.restaurant_name}</p>
                        <p className="text-xs text-muted-foreground mt-1">{o.zone_name ? `Zone · ${o.zone_name}` : "No zone assigned"}</p>
                        <ContactRow phone={o.restaurant_phone} testidPrefix="rest" />
                    </Section>

                    <RiderCard order={o} />

                    {tenant?.shiprocket_enabled && <ShiprocketCard order={o} onChange={load} />}

                    <Section title="Timeline" testid="timeline">
                        <ol className="space-y-2">
                            {(o.events || []).map((e, idx) => (
                                <li key={idx} className="text-xs flex items-start gap-2">
                                    <span className="w-2 h-2 mt-1.5 rounded-full bg-primary shrink-0" />
                                    <div>
                                        <p className="font-semibold">{e.status}</p>
                                        <p className="text-muted-foreground">{new Date(e.ts).toLocaleString()} · {e.actor}</p>
                                        {e.message && <p className="text-muted-foreground">{e.message}</p>}
                                    </div>
                                </li>
                            ))}
                            {(!o.events || o.events.length === 0) && <p className="text-xs text-muted-foreground">No events yet.</p>}
                        </ol>
                    </Section>
                </div>
            </div>
        </div>
    );
}

const RETURN_TONE = { PENDING: "amber", APPROVED: "emerald", REJECTED: "rose" };

/**
 * Return request summary.
 *
 * A return is a side record on the order rather than an order status
 * (models.OrderReturn), so nothing about it used to reach this page —
 * an operator looking at an order had no way to tell a return had been
 * filed against it, let alone why or what was refunded. Renders nothing
 * when the order has no return, so orders without one are unchanged.
 */
function ReturnCard({ ret }) {
    if (!ret) return null;
    const tone = RETURN_TONE[ret.status] || "amber";
    const when = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
    return (
        <Section title="Return request" testid="return-card" tone={tone} icon={<Undo2 size={14} />}>
            <div className="flex items-center gap-2 flex-wrap">
                <Badge tone={tone} data-testid="return-status-badge">{ret.status}</Badge>
                {ret.requested_at && (
                    <span className="text-xs text-muted-foreground">Requested {when(ret.requested_at)}</span>
                )}
            </div>

            <div className="mt-3">
                <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Customer&apos;s reason</p>
                <p data-testid="return-reason" className="text-sm">{ret.reason || "—"}</p>
            </div>

            {ret.status === "REJECTED" && ret.reject_reason && (
                <div className="mt-3">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Rejection reason</p>
                    <p data-testid="return-reject-reason" className="text-sm">{ret.reject_reason}</p>
                </div>
            )}

            <div className="mt-3 border-t border-border/60 pt-2">
                {ret.status === "APPROVED" && (
                    <Row k="Refunded to wallet" v={<span data-testid="return-refund-amount">{inr(ret.refund_amount || 0)}</span>} />
                )}
                {ret.processed_at && <Row k="Reviewed" v={when(ret.processed_at)} />}
            </div>

            {ret.status === "PENDING" && (
                <Link to="/admin/returns" data-testid="return-review-link"
                    className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-primary">
                    Review in Returns queue →
                </Link>
            )}
        </Section>
    );
}

function Section({ title, children, testid, icon, tone }) {
    const tones = {
        violet: "bg-violet-50 border-violet-200",
        amber: "bg-amber-50 border-amber-200",
        emerald: "bg-emerald-50 border-emerald-200",
        rose: "bg-rose-50 border-rose-200",
    };
    const cls = tone ? tones[tone] : "bg-card border-border";
    return (
        <div data-testid={testid} className={`border rounded-2xl p-4 ${cls}`}>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2 inline-flex items-center gap-1">{icon}{title}</p>
            {children}
        </div>
    );
}

/**
 * Inline action row for tap-to-call and tap-to-navigate (Google Maps).
 *
 * iter134w — previously used a plain ``<a href="https://...maps/dir/...
 * ?target=_blank">``. Inside a Capacitor WebView ``target="_blank"``
 * routes through Chrome's ``intent://`` fallback, which the embedded
 * WebView can't dispatch — producing the user-reported
 * ``ERR_UNKNOWN_URL_SCHEME`` blank page. The rider app already solved
 * this via the ``openMap()`` helper in ``lib/openMap.js`` (native
 * AppLauncher → geo:/https deep-link → Custom Tabs → last-resort
 * ``location.href``). We now use the same helper so admin Navigate
 * buttons behave identically on Android + iOS Capacitor and PWAs.
 *
 * ``mapTo`` shape (unchanged from before):
 *   - ``lat`` + ``lng`` (preferred — coordinate-based, no name search)
 *   - ``label`` fallback (address / shop-name string) when coords absent
 */
function ContactRow({ phone, mapTo, testidPrefix }) {
    if (!phone && !mapTo) return null;
    const canNavigate =
        mapTo && ((mapTo.lat && mapTo.lng) || mapTo.label);
    const handleNavigate = async (e) => {
        e.preventDefault();
        // openMap() gracefully handles missing coordinates by falling
        // back to a label search — matches the rider app's contract
        // and matches the previous <a href> behaviour of this button.
        await openMap({
            lat: mapTo?.lat,
            lng: mapTo?.lng,
            label: mapTo?.label,
            address: mapTo?.address,
        });
    };
    return (
        <div className="flex items-center gap-2 mt-3">
            {phone && (
                <CallButton phone={phone} testid={`${testidPrefix}-call`} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl py-2 text-xs font-bold">
                    <Phone size={13} /> Call
                </CallButton>
            )}
            {canNavigate && (
                <button
                    type="button"
                    data-testid={`${testidPrefix}-navigate`}
                    onClick={handleNavigate}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-sky-50 text-sky-700 border border-sky-200 rounded-xl py-2 text-xs font-bold"
                >
                    <Navigation size={13} /> Navigate
                </button>
            )}
        </div>
    );
}

/**
 * Rider card — surfaces assigned rider (or a placeholder if not assigned yet)
 * plus contact + maps quick-actions for both restaurant pickup and customer drop-off.
 */
function RiderCard({ order }) {
    const adr = order.delivery_address || {};
    const restLatLng = order.restaurant_location || order.pickup_location || null;
    const riderPhone = order.rider_mobile;
    return (
        <Section title="Rider" testid="rider-card" icon={<Truck size={14} />} tone="violet">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-violet-200 text-violet-800 flex items-center justify-center"><Truck size={16} /></div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold">{order.rider_name || "Awaiting assignment"}</p>
                    <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5"><Phone size={11} /> {riderPhone || "—"}</p>
                </div>
            </div>
            <ContactRow phone={riderPhone} testidPrefix="rider" />

            <div className="border-t border-violet-200/60 mt-3 pt-3 space-y-2">
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Pickup &amp; drop quick links</p>
                <div className="flex items-center gap-2">
                    <span className="text-xs flex-1">
                        <strong>Pickup:</strong> {order.restaurant_name}
                    </span>
                    <ContactRow
                        phone={order.restaurant_phone}
                        mapTo={restLatLng ? { lat: restLatLng.lat, lng: restLatLng.lng, label: order.restaurant_name } : { label: order.restaurant_name }}
                        testidPrefix="pickup"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs flex-1">
                        <strong>Drop:</strong> {order.customer_name || "Customer"}
                    </span>
                    <ContactRow
                        phone={order.customer_mobile}
                        mapTo={{ lat: adr.lat, lng: adr.lng, label: [adr.line1, adr.city, adr.pincode].filter(Boolean).join(", ") }}
                        testidPrefix="drop"
                    />
                </div>
            </div>
        </Section>
    );
}

/**
 * Shiprocket card — only rendered when the Super Admin has enabled
 * Shiprocket for this tenant (``tenant.shiprocket_enabled``, checked
 * by the caller). Mirrors the credential-config page's error handling
 * (``lib/api``/toast pattern) against the already-existing backend
 * endpoints:
 *   - POST /admin/shiprocket/orders/{id}/create-shipment — pushes this
 *     order to Shiprocket; the response is also persisted onto
 *     ``order.shiprocket`` server-side, so a reload (``onChange``)
 *     picks it up without re-deriving state here.
 *   - GET /admin/shiprocket/orders/{id}/tracking — pull-based status
 *     refresh once an AWB exists.
 * These endpoints existed already (see AdminShiprocket.jsx for the
 * credential-setup half of the feature) but had no caller anywhere in
 * the app — this is that missing caller, not a new integration.
 */
function ShiprocketCard({ order, onChange }) {
    // Same double-click race as the order-status buttons above — a
    // duplicate "Send to Shiprocket" click could create two shipments
    // for one order. useSubmitGuard's ref check closes the gap that
    // `disabled={busy}` state alone leaves open.
    const [busy, run] = useSubmitGuard();
    const [tracking, setTracking] = useState(null);
    const sr = order.shiprocket || null;

    const send = () => run(async () => {
        try {
            await api.post(`/admin/shiprocket/orders/${order.id}/create-shipment`);
            toast.success("Order sent to Shiprocket");
            await onChange();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(d?.message || (typeof d === "string" ? d : "Could not send to Shiprocket"));
        }
    });

    const refreshTracking = () => run(async () => {
        try {
            const { data } = await api.get(`/admin/shiprocket/orders/${order.id}/tracking`);
            setTracking(data);
            // Bug found in production (mani tenant): this used to only set
            // local ``tracking`` state for the raw JSON dump below — it
            // never reloaded ``order``, so even after the backend fix that
            // persists the live status, the already-rendered "Status" row
            // kept showing whatever was loaded when the page first opened
            // (e.g. "NEW" forever, even after cancelling the shipment
            // directly in Shiprocket). Reload the order the same way
            // send()/assignAwb() already do so the Status row reflects
            // what was just fetched.
            await onChange();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(d?.message || (typeof d === "string" ? d : "Could not fetch tracking"));
        }
    });

    // Small, stable set from shiprocket_service.map_shiprocket_status() —
    // used only for badge colour; the raw text (whatever Shiprocket
    // actually said, e.g. "Canceled") is still what's displayed.
    const STATUS_TONE = {
        CANCELED: "text-red-700 dark:text-red-400",
        RTO: "text-red-700 dark:text-red-400",
        RETURNED: "text-red-700 dark:text-red-400",
        LOST_DAMAGED: "text-red-700 dark:text-red-400",
        EXCEPTION: "text-amber-700 dark:text-amber-400",
        NDR: "text-amber-700 dark:text-amber-400",
        DELIVERED: "text-emerald-700 dark:text-emerald-400",
        OUT_FOR_DELIVERY: "text-emerald-700 dark:text-emerald-400",
        IN_TRANSIT: "text-sky-700 dark:text-sky-400",
        PICKED_UP: "text-sky-700 dark:text-sky-400",
        PICKUP_SCHEDULED: "text-amber-700 dark:text-amber-400",
        NEW: "text-muted-foreground",
    };
    // Mirrors shiprocket_service.TERMINAL_DEAD_STATUSES — a shipment
    // here will never progress further, so create_shipment()'s
    // duplicate-guard on the backend allows send() through again for
    // exactly these statuses (see that guard's own comment).
    const TERMINAL_STATUSES = new Set(["CANCELED", "RTO", "RETURNED", "LOST_DAMAGED"]);

    const assignAwb = () => run(async () => {
        try {
            await api.post(`/admin/shiprocket/orders/${order.id}/assign-awb`);
            toast.success("Courier assigned");
            await onChange();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(d?.message || (typeof d === "string" ? d : "Could not assign a courier"));
        }
    });

    // Bug found in production (mani tenant): this used to gate on
    // ``sr?.awb_code`` alone, so a shipment that Shiprocket genuinely
    // created (real shiprocket_order_id/shipment_id) but hasn't
    // assigned a courier/AWB to yet — the normal, expected state right
    // after "Send to Shiprocket", since AWB assignment is a separate
    // Shiprocket API step — looked IDENTICAL to "never sent at all".
    // The admin saw a success toast, then the exact same "Send to
    // Shiprocket" button, with nothing to indicate anything happened
    // (and no guard stopping them from sending it again). Gate on
    // ``shiprocket_order_id`` instead, and render a distinct
    // in-between state.
    const created = !!sr?.shiprocket_order_id;
    const hasAwb = !!sr?.awb_code;
    // Required change: once a shipment reaches a dead end (cancelled
    // directly in Shiprocket, RTO'd, lost/damaged), refreshing tracking
    // or assigning a courier again is pointless — offer a fresh
    // shipment instead. The backend's duplicate-guard already allows
    // send() through again in exactly this case (see create_shipment's
    // comment in routers/admin/shiprocket.py).
    const isTerminal = created && TERMINAL_STATUSES.has(sr?.status_mapped);

    return (
        <Section title="Shiprocket" testid="shiprocket-card" icon={<PackageCheck size={14} />}>
            {!created ? (
                <>
                    <p className="text-xs text-muted-foreground mb-3">
                        This order hasn&apos;t been shipped via Shiprocket yet.
                    </p>
                    <button
                        type="button"
                        data-testid="shiprocket-send-btn"
                        disabled={busy}
                        onClick={send}
                        className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl py-2 text-xs font-bold disabled:opacity-60"
                    >
                        {busy && <Loader2 size={13} className="animate-spin" />}
                        Send to Shiprocket
                    </button>
                </>
            ) : (
                <>
                    <Row k="Shiprocket order" v={<span data-testid="shiprocket-order-id">{sr.shiprocket_order_id}</span>} />
                    {hasAwb && <Row k="AWB" v={<span data-testid="shiprocket-awb">{sr.awb_code}</span>} />}
                    {sr.courier_name && <Row k="Courier" v={sr.courier_name} />}
                    {sr.status && (
                        <Row k="Status" v={
                            <span data-testid="shiprocket-status" className={`font-bold ${STATUS_TONE[sr.status_mapped] || ""}`}>
                                {sr.status}
                            </span>
                        } />
                    )}
                    {isTerminal ? (
                        <>
                            <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/40 rounded-xl p-3 mt-2 mb-3" data-testid="shiprocket-terminal-banner">
                                <p className="text-xs font-bold text-red-800 dark:text-red-300">
                                    This shipment {sr.status_mapped === "CANCELED" ? "was cancelled" : "will not be delivered"} and can&apos;t be tracked further.
                                </p>
                                <p className="text-[11px] text-red-700/80 dark:text-red-400/80 mt-1">
                                    Create a new shipment below if this order still needs to ship — it gets its own new AWB.
                                </p>
                            </div>
                            <button
                                type="button"
                                data-testid="shiprocket-recreate-btn"
                                disabled={busy}
                                onClick={send}
                                className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl py-2 text-xs font-bold disabled:opacity-60"
                            >
                                {busy && <Loader2 size={13} className="animate-spin" />}
                                Create New Shipment
                            </button>
                        </>
                    ) : !hasAwb ? (
                        <>
                            <p className="text-xs text-muted-foreground mt-2 mb-3">
                                Created in Shiprocket — no courier/AWB assigned yet.
                            </p>
                            <button
                                type="button"
                                data-testid="shiprocket-assign-awb-btn"
                                disabled={busy}
                                onClick={assignAwb}
                                className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl py-2 text-xs font-bold disabled:opacity-60"
                            >
                                {busy && <Loader2 size={13} className="animate-spin" />}
                                Assign courier / Get AWB
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                data-testid="shiprocket-refresh-btn"
                                disabled={busy}
                                onClick={refreshTracking}
                                className="w-full mt-2 inline-flex items-center justify-center gap-2 border rounded-xl py-2 text-xs font-bold disabled:opacity-60"
                            >
                                {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                                Refresh tracking
                            </button>
                            {tracking && (
                                <pre className="text-[11px] bg-muted/50 rounded-lg p-2 mt-2 overflow-x-auto whitespace-pre-wrap" data-testid="shiprocket-tracking-raw">
                                    {JSON.stringify(tracking, null, 2)}
                                </pre>
                            )}
                        </>
                    )}
                </>
            )}
        </Section>
    );
}

function Row({ k, v }) {
    return <div className="flex items-center justify-between py-1.5 text-sm"><span className="text-muted-foreground">{k}</span><span className="font-medium">{v}</span></div>;
}

function Badge({ children, tone, ...rest }) {
    const tones = {
        sky: "bg-sky-100 text-sky-700",
        violet: "bg-violet-100 text-violet-700",
        emerald: "bg-emerald-100 text-emerald-700",
        rose: "bg-rose-100 text-rose-700",
    };
    const cls = tone ? tones[tone] : "bg-amber-100 text-amber-700";
    // Forward extra props (``data-testid`` in particular) — callers were
    // silently losing them before.
    return <span {...rest} className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${cls}`}>{children}</span>;
}

export { Section as OrderDetailSection, Row as OrderDetailRow, Badge as OrderDetailBadge };
